import { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import PlayPauseButton from '../common/PlayPauseButton'
import { highlightCampMask, volunteerBanner } from '../../assets/index'
import { HOME_MOBILE_MQ } from './homeMobileLayout'
import { useHighlightScrollZoom } from '../../hooks/useHighlightScrollZoom'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { getScrollHeaderOffset } from '../../utils/scrollAnimation'
import {
  HIGHLIGHT_DESIGN_WIDTH,
  HIGHLIGHT_DESIGN_HEIGHT,
  HIGHLIGHT_INITIAL_MASK_SIZE,
  getHighlightLayoutScale,
} from './highlightVideoLayout'

const DUMMY_VIDEO_SRC =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4'

const DESIGN_WIDTH = HIGHLIGHT_DESIGN_WIDTH
const DESIGN_HEIGHT = HIGHLIGHT_DESIGN_HEIGHT
const MASK_SIZE = HIGHLIGHT_INITIAL_MASK_SIZE

function MaskedVideo({ videoRef, onPlayStateChange, sx = {}, preload = 'metadata' }) {
  return (
    <Box
      component="video"
      ref={videoRef}
      src={DUMMY_VIDEO_SRC}
      poster={volunteerBanner}
      playsInline
      muted
      preload={preload}
      onPlay={() => onPlayStateChange(true)}
      onPause={() => onPlayStateChange(false)}
      onEnded={() => onPlayStateChange(false)}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        display: 'block',
        ...sx,
      }}
    />
  )
}

function HighlightVideoCanvas({ videoRef, isPlaying, onToggle, onPlayStateChange }) {
  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          WebkitMaskImage: `url(${highlightCampMask})`,
          maskImage: `url(${highlightCampMask})`,
          WebkitMaskSize: `${MASK_SIZE}px ${MASK_SIZE}px`,
          maskSize: `${MASK_SIZE}px ${MASK_SIZE}px`,
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          overflow: 'hidden',
        }}
      >
        <MaskedVideo
          videoRef={videoRef}
          onPlayStateChange={onPlayStateChange}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
        }}
      >
        <PlayPauseButton isPlaying={isPlaying} onClick={onToggle} />
      </Box>
    </>
  )
}

function ScaledHighlightLayout({ videoRef, isPlaying, onToggle, onPlayStateChange }) {
  const containerRef = useRef(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return
      const width = containerRef.current.offsetWidth
      setScale(Math.min(width / DESIGN_WIDTH, 1))
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        maxWidth: DESIGN_WIDTH,
        mx: 'auto',
        height: DESIGN_HEIGHT * scale,
        overflow: 'hidden',
        bgcolor: '#fffefa',
      }}
    >
      <Box
        sx={{
          width: DESIGN_WIDTH,
          height: DESIGN_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
          bgcolor: '#fffefa',
        }}
      >
        <HighlightVideoCanvas
          videoRef={videoRef}
          isPlaying={isPlaying}
          onToggle={onToggle}
          onPlayStateChange={onPlayStateChange}
        />
      </Box>
    </Box>
  )
}

function ScrollZoomHighlightLayout({
  videoRef,
  isPlaying,
  onToggle,
  onPlayStateChange,
  trackRef,
  pinRef,
  layerRefs,
  onMetricsReady,
}) {
  const stageRef = useRef(null)
  const [layoutScale, setLayoutScale] = useState(1)

  const getMaskMetrics = useCallback(() => {
    if (!stageRef.current) return null
    const width = stageRef.current.offsetWidth
    return {
      layoutScale: getHighlightLayoutScale(width),
    }
  }, [])

  useEffect(() => {
    const updateScale = () => {
      if (!stageRef.current) return
      const width = stageRef.current.offsetWidth
      setLayoutScale(getHighlightLayoutScale(width))
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    if (stageRef.current) observer.observe(stageRef.current)
    window.addEventListener('resize', updateScale)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateScale)
    }
  }, [])

  useEffect(() => {
    if (onMetricsReady) onMetricsReady(getMaskMetrics)
  }, [getMaskMetrics, onMetricsReady, layoutScale])

  return (
    <Box ref={trackRef} sx={{ position: 'relative', width: '100%' }}>
      <Box
        ref={pinRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: { lg: `calc(100vh - ${getScrollHeaderOffset()}px)` },
          overflow: 'hidden',
          bgcolor: '#fffefa',
          zIndex: 5,
          boxSizing: 'border-box',
        }}
      >
        <Box
          ref={stageRef}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: DESIGN_WIDTH,
              height: DESIGN_HEIGHT,
              transform: `scale(${layoutScale})`,
              transformOrigin: 'top left',
              position: 'relative',
              bgcolor: '#fffefa',
            }}
          >
            <Box
              ref={(node) => {
                if (layerRefs) layerRefs.current.videoMask = node
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                WebkitMaskImage: `url(${highlightCampMask})`,
                maskImage: `url(${highlightCampMask})`,
                WebkitMaskSize: `${MASK_SIZE}px ${MASK_SIZE}px`,
                maskSize: `${MASK_SIZE}px ${MASK_SIZE}px`,
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                overflow: 'hidden',
                willChange: 'mask-size, -webkit-mask-size, transform',
                backfaceVisibility: 'hidden',
              }}
            >
              <MaskedVideo
                videoRef={videoRef}
                onPlayStateChange={onPlayStateChange}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                pointerEvents: 'auto',
              }}
            >
              <PlayPauseButton isPlaying={isPlaying} onClick={onToggle} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function MobileHighlightLayout({ videoRef, isPlaying, onToggle, onPlayStateChange }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: { xs: '402 / 485', sm: '1 / 1' },
        bgcolor: '#111',
        overflow: 'hidden',
        [HOME_MOBILE_MQ]: { aspectRatio: '402 / 485', minHeight: 485 },
      }}
    >
      <MaskedVideo
        videoRef={videoRef}
        onPlayStateChange={onPlayStateChange}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectPosition: 'center',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
        }}
      >
        <PlayPauseButton isPlaying={isPlaying} onClick={onToggle} />
      </Box>
    </Box>
  )
}

export default function HighlightVideoBanner({ enableScrollZoom = false }) {
  const desktopVideoRef = useRef(null)
  const mobileVideoRef = useRef(null)
  const trackRef = useRef(null)
  const pinRef = useRef(null)
  const layerRefs = useRef({
    videoMask: null,
  })
  const getMaskMetricsRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handleMetricsReady = useCallback((getMetrics) => {
    getMaskMetricsRef.current = getMetrics
  }, [])
  const reducedMotion = useReducedMotion()

  const scrollZoomActive = enableScrollZoom && !reducedMotion
  const getMetrics = useCallback(() => getMaskMetricsRef.current?.() ?? null, [])
  useHighlightScrollZoom(
    trackRef,
    pinRef,
    layerRefs,
    getMetrics,
    desktopVideoRef,
    scrollZoomActive,
  )

  const togglePlayback = async () => {
    const video = desktopVideoRef.current ?? mobileVideoRef.current
    if (!video) return

    if (video.paused) {
      try {
        video.muted = false
        await video.play()
        setIsPlaying(true)
      } catch {
        setIsPlaying(false)
      }
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        bgcolor: '#fffefa',
        overflow: scrollZoomActive ? 'visible' : 'hidden',
      }}
    >
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        {scrollZoomActive ? (
          <ScrollZoomHighlightLayout
            videoRef={desktopVideoRef}
            isPlaying={isPlaying}
            onToggle={togglePlayback}
            onPlayStateChange={setIsPlaying}
            trackRef={trackRef}
            pinRef={pinRef}
            layerRefs={layerRefs}
            onMetricsReady={handleMetricsReady}
          />
        ) : (
          <ScaledHighlightLayout
            videoRef={desktopVideoRef}
            isPlaying={isPlaying}
            onToggle={togglePlayback}
            onPlayStateChange={setIsPlaying}
          />
        )}
      </Box>
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <MobileHighlightLayout
          videoRef={mobileVideoRef}
          isPlaying={isPlaying}
          onToggle={togglePlayback}
          onPlayStateChange={setIsPlaying}
        />
      </Box>
    </Box>
  )
}
